/**
 * Scadenzario AI: mostra eventi/scadenze rilevati dalle email (suggerimenti) e
 * permette di aggiungerli al calendario o ignorarli. "Cerca scadenze" avvia una
 * scansione on-demand delle email recenti.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Sparkles, Check, X, Loader2, MapPin } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import { calendarApi, type CalendarSuggestion } from '@/lib/api/endpoints';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api/client';

export function CalendarSuggestions() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['calendar-suggestions'], queryFn: () => calendarApi.pending() });
  const items = q.data?.events ?? [];

  const scanMut = useMutation({
    mutationFn: () => calendarApi.scan(),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['calendar-suggestions'] });
      toast.success(r.found > 0 ? `${r.found} scadenze trovate` : 'Nessuna nuova scadenza nelle email recenti');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Scansione fallita'),
  });

  const confirmMut = useMutation({
    mutationFn: (id: string) => calendarApi.confirmSuggestion(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['calendar-suggestions'] }); qc.invalidateQueries({ queryKey: ['calendar'] }); toast.success('Aggiunto al calendario'); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Errore'),
  });
  const dismissMut = useMutation({
    mutationFn: (id: string) => calendarApi.dismissSuggestion(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar-suggestions'] }),
  });

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between px-4 py-2.5 border-b">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <CalendarClock className="h-4 w-4 text-primary" /> Scadenze dalle email{items.length > 0 ? ` · ${items.length}` : ''}
        </div>
        <Button variant="outline" size="sm" onClick={() => scanMut.mutate()} disabled={scanMut.isPending}>
          {scanMut.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
          Cerca scadenze
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="px-4 py-6 text-xs text-muted-foreground text-center">
          Nessuna scadenza in sospeso. Premi “Cerca scadenze” per analizzare le email recenti.
        </div>
      ) : (
        <div className="divide-y">
          {items.map((e: CalendarSuggestion) => (
            <div key={e.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{e.title}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {format(parseISO(e.start_iso), "EEEE d MMMM 'alle' HH:mm", { locale: it })}
                  {e.location ? <span className="inline-flex items-center gap-0.5 ml-2"><MapPin className="h-3 w-3" /> {e.location}</span> : null}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => confirmMut.mutate(e.id)} disabled={confirmMut.isPending}>
                  <Check className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Aggiungi
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => dismissMut.mutate(e.id)} title="Ignora">
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
